import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { apiError } from "@/lib/api";
import { Droplet } from "lucide-react";
import { toast } from "sonner";

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ username: "", email: "", password: "", name: "", agree_rules: false, hp: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const upd = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.agree_rules) {
      setError("Please agree to the community rules to join.");
      return;
    }
    setLoading(true);
    try {
      await register(form);
      toast.success("Account created. Happy collecting!");
      navigate("/");
    } catch (err) {
      setError(apiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="hidden lg:flex flex-col justify-between p-12 relative overflow-hidden" style={{ background: "linear-gradient(150deg, #451a03, #78350f 55%, #b45309)" }}>
        <Link to="/" className="flex items-center gap-2 text-amber-100 relative z-10">
          <Droplet className="w-7 h-7" style={{ color: "#F59E0B" }} fill="#F59E0B" />
          <span className="font-display text-3xl font-bold">Drops of Honey</span>
        </Link>
        <div className="relative z-10 text-amber-50">
          <h2 className="font-display text-5xl font-bold leading-tight">Join a community of insulator collectors.</h2>
          <p className="mt-4 text-amber-100/80 max-w-md">Track your collection value, grade pieces, trade in the forum, and message fellow hobbyists.</p>
        </div>
        <div className="absolute -bottom-20 -right-20 w-96 h-96 rounded-full" style={{ background: "radial-gradient(circle, rgba(245,158,11,0.35), transparent 70%)" }} />
      </div>

      <div className="flex items-center justify-center p-6 sm:p-12">
        <form onSubmit={submit} className="w-full max-w-sm float-up" data-testid="register-form">
          <h1 className="font-display text-4xl font-bold">Create account</h1>
          <p className="text-muted-page mt-1 mb-6">It's free to start your collection.</p>

          {error && <div data-testid="register-error" className="mb-4 text-sm bg-rose-50 text-rose-700 border border-rose-200 rounded-lg px-3 py-2">{error}</div>}

          <input type="text" name="company_website" tabIndex={-1} autoComplete="off" aria-hidden="true" value={form.hp} onChange={upd("hp")} style={{ position: "absolute", left: "-9999px", width: 1, height: 1, opacity: 0 }} />

          <label className="block text-xs font-tech uppercase tracking-widest text-muted-page mb-1">Username</label>
          <input data-testid="register-username-input" required value={form.username} onChange={upd("username")}
            className="w-full surface border rounded-lg px-3 py-2.5 mb-4 focus:outline-none focus:ring-2 focus:ring-amber-400" />

          <label className="block text-xs font-tech uppercase tracking-widest text-muted-page mb-1">Email</label>
          <input data-testid="register-email-input" type="email" required value={form.email} onChange={upd("email")}
            className="w-full surface border rounded-lg px-3 py-2.5 mb-4 focus:outline-none focus:ring-2 focus:ring-amber-400" />

          <label className="block text-xs font-tech uppercase tracking-widest text-muted-page mb-1">Password</label>
          <input data-testid="register-password-input" type="password" required minLength={6} value={form.password} onChange={upd("password")}
            className="w-full surface border rounded-lg px-3 py-2.5 mb-4 focus:outline-none focus:ring-2 focus:ring-amber-400" />

          <label className="flex items-start gap-2.5 mb-6 cursor-pointer">
            <input data-testid="register-agree-rules" type="checkbox" required checked={form.agree_rules} onChange={(e) => setForm((f) => ({ ...f, agree_rules: e.target.checked }))} className="mt-0.5 w-4 h-4 accent-amber-600" />
            <span className="text-sm text-muted-page">I agree to the <Link to="/rules" target="_blank" className="brand-text font-semibold honey-underline">community rules</Link> — be respectful, no harassment, belittling, or profanity.</span>
          </label>

          <button data-testid="register-submit-btn" disabled={loading} className="w-full py-3 rounded-full font-semibold disabled:opacity-60" style={{ background: "var(--brand)", color: "#fff" }}>
            {loading ? "Creating…" : "Create account"}
          </button>
          <p className="text-sm text-muted-page mt-4 text-center">
            Already a member? <Link to="/login" className="brand-text font-semibold honey-underline">Sign in</Link>
          </p>
        </form>
      </div>
    </div>
  );
}
